<template>
  <div class="location-avatar" :class="avatarClass" :style="avatarStyle">
    <img v-if="showImage" :src="imageSrc" :alt="props.name" @error="imageFailed = true" />
    <span v-else-if="initials" class="location-avatar__initials" aria-hidden="true">{{ initials }}</span>
    <IconEmbedded v-else name="map-pin_1-5" :size="Math.round(props.size * 0.55)" color="currentColor" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import { getAvatarColor, getBusinessInitials } from "@logic/businessInitials";

const props = withDefaults(
  defineProps<{
    src?: string | null;
    name?: string;
    size?: number;
    placeholderTone?: "teal" | "gray";
  }>(),
  { src: null, name: "", size: 48, placeholderTone: "gray" }
);

const imageFailed = ref(false);
watch(
  () => props.src,
  () => (imageFailed.value = false)
);

const imageSrc = computed(() => {
  const value = props.src?.trim();
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  const contentBaseUri = globalThis.Bootstrap?.Config?.contentCdnBaseUri ?? "";
  if (!contentBaseUri) return value;
  const iconId = value.replace(/^\/+/, "");
  return `${contentBaseUri.replace(/\/$/, "")}/cms/images/expeditions/location-icons/${iconId}`;
});

const showImage = computed(() => Boolean(imageSrc.value) && !imageFailed.value);
const initials = computed(() => getBusinessInitials(props.name));
const avatarColor = computed(() => getAvatarColor(props.name));

const avatarClass = computed(() => ({
  "location-avatar--gray": !showImage.value && !initials.value && props.placeholderTone === "gray",
}));

const avatarStyle = computed(() => {
  if (showImage.value || !initials.value) return undefined;
  return {
    background: avatarColor.value.background,
    color: avatarColor.value.foreground,
  };
});
</script>

<style scoped lang="scss">
.location-avatar {
  width: v-bind("`${props.size}px`");
  height: v-bind("`${props.size}px`");
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 999px;
  color: white;
  background: #176c62;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &--gray {
    color: #667085;
    background: #e4e7ec;
  }

  &__initials {
    font-family: inherit;
    font-weight: 500;
    font-size: v-bind("`${Math.round(props.size * 0.38)}px`");
    line-height: 1;
    letter-spacing: 0.02em;
    user-select: none;
  }
}
</style>
